import { NAME_VALIDATION_DEBOUNCE_TIME } from '@/core/utils/utils';
import { PropType } from 'vue';
import { mapGetters } from 'vuex';
import debounce from 'lodash.debounce';
import { Vue, Options } from 'vue-class-component';
import { EPDeviceTypeEnum, GateConfigDTO, GateDTO } from '@/../client-generator/generated-clients/plants';
import { FETCH_PLANT } from '@/core/utils/emitter-events';
import { SET_APP_LOADING } from '@/core/store/action-types';
import { NamingService } from '@/core/services/naming.service';
import DialogAddEditGate from '../../EntrancePlantGate/DialogAddEditGate/DialogAddEditGate.vue';
import plantsClient from '@/core/services/plants';

@Options({
  name: 'EntrancePlantDashboardGate',
  emits: ['add-object', 'update-list'],
  props: {
    gate: {
      type: Object as PropType<GateDTO>,
      required: false,
    },
    plantId: {
      type: Number,
      required: true,
    },
    showActionButton: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
      default: false,
    }
  },
  components: {
    DialogAddEditGate
  },
  watch: {
    gate: function () {
      this.updateCounts();
    },
    'gate.name': function () {
      this.verifyValidityOfName();
    },
  },
  computed: {
    ...mapGetters('UI', ['plantInlineRenameEnabled']),
  },
})
export default class EntrancePlantDashboardGate extends Vue {
  gate!: GateDTO;
  plantId!: number;
  showActionButton!: boolean;
  disabled!: boolean;
  plantInlineRenameEnabled!: boolean;
  isValidName = true;
  initialName!: string;
  invalidNameReason: string = '';
  gateToEdit: GateDTO | null = null;
  isEditGateDialogVisible = false;

  get gateDeviceCount() {
    return this.gate.devices?.length ?? 0;
  }

  async created() {
    this.verifyValidityOfName = debounce(
      this.verifyValidityOfName,
      NAME_VALIDATION_DEBOUNCE_TIME + 1000
    );
  }

  mounted() {
    this.updateCounts();
    this.initialName = this.gate.name!;
  }

  private async verifyValidityOfName() {
    if (this.gate.name === this.initialName) return;
    const validName = NamingService.isValidEntityTypeName(this.gate.name);

    if (this.initialName !== this.gate.name && !validName) {
      this.$store.dispatch(SET_APP_LOADING, true);

      this.isValidName = validName;
      this.invalidNameReason = this.$t(
        `uniqueNaming.validationErrorWithSuggestion`
      );
      this.$store.dispatch(SET_APP_LOADING, false);
    } else {
      this.isValidName = true;
      this.invalidNameReason = '';
      await this.updateGateName();
    }
  }

  private async updateGateName() {
    try {
      this.$store.dispatch(SET_APP_LOADING, true);
      const gateToUpdate = {[this.gate.gateId!]: this.getDataToUpdate()};
      await plantsClient.gatePUT(this.plantId, gateToUpdate);
      this.$toast.add({
        severity: 'success',
        summary: this.$t('uniqueNaming.roomInlineNamingSuccess'),
        life: 3000,
      });
      NamingService.forcefullyRenameFirstEntityWithinCachedOnes(
        this.initialName,
        this.gate.name!
      );
      this.initialName = this.gate.name!;
    } catch (e) {
      this.gate.name = this.initialName;
      this.$toast.add({
        severity: 'error',
        summary: this.$t('uniqueNaming.roomInlineNamingError'),
        life: 3000,
      });
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
      this.$emitter.emit(FETCH_PLANT);
    }
  }

  onHideNameValidationDialog() {
    this.gate.name = this.initialName;
    this.isValidName = true;
  }

  updateCounts() {}

  toggleEditGateDialog(gate: any) {
    this.gateToEdit = gate;
    this.isEditGateDialogVisible = !this.isEditGateDialogVisible;
  }

  handleUpdateList() {
    this.$emit('update-list');
  }

  goToGateDetail() {
    this.$router.push({
      name: 'page-entranceplant-gate',
      params: {
        gateId: this.gate.gateId ?? '',
      },
    });
  }

  getDataToUpdate() {
    const gateConfig = new GateConfigDTO({
      scsAddress: this.gate.scsAddress,
      gateName: this.gate.name,
      newPeSelected: this.gate.devices?.some(
        (device) => device.type === EPDeviceTypeEnum.Linea5000
      ),
      keypadSelected: this.gate.devices?.some(
        (device) => device.type === EPDeviceTypeEnum.KeyPad
      ),
      smartReaderSelected: this.gate.devices?.some(
        (device) => device.type === EPDeviceTypeEnum.SmartAccess
      ),
      connectedBuildingIds: this.gate.connectedBuildingIds,
      connectedRiserIds: this.gate.connectedRiserIds,
    })
      
    return gateConfig;
  }
}
